/**
 * Direct Browser → Dropbox chunked upload — HARDENED version.
 *
 * Why this file exists
 * --------------------
 * Until v1.11.x staff videos went Browser → Cloud Run → Dropbox: a 200MB
 * MP4 caused a 30-second OOM on Cloud Run that knocked everyone off the
 * portal. This module replaces that with the 3-step "ticket" protocol:
 *
 *   1. Backend: POST /api/staff/uploads/start  →  ticket + access_token + path
 *   2. Browser: chunked PUT to content.dropboxapi.com (8 MB chunks)
 *   3. Backend: POST /api/staff/uploads/finalize  →  DB record (message etc.)
 *
 * Hardening (v1.22 — "video Iula da 600MB sparito")
 * -------------------------------------------------
 * A 600 MB video = ~75 chunks over a mobile connection: one network blip
 * used to kill the whole upload with zero retry. Now:
 *   - every chunk is retried up to 4 times with exponential backoff;
 *   - Dropbox `incorrect_offset` responses are used to RESUME from the
 *     byte the server actually has (a chunk that "failed" client-side
 *     may have landed server-side);
 *   - the finalize call is retried too — and if it still fails AFTER the
 *     file landed on Dropbox, the thrown error carries
 *     `uploadedToDropbox = true` so the UI can reassure the user (the
 *     hourly backend reconciler will register the file automatically).
 *
 * The Dropbox token shipped to the browser is short-lived (~3-4h) and
 * scoped by the Dropbox App Folder. The refresh_token never leaves the
 * backend.
 *
 * Public API (unchanged)
 * ----------------------
 * uploadToDropbox({ kind, file, clientId, targetId, onProgress })
 * uploadViaMagicLink({ magicToken, file, onProgress })
 */
import { api, apiError } from "./api";

const DROPBOX_CONTENT = "https://content.dropboxapi.com";

const MAX_CHUNK_ATTEMPTS = 4;
const RETRYABLE_STATUS = new Set([429, 500, 502, 503, 504]);

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Dropbox-API-Arg viaggia come header HTTP, e i browser ammettono solo
// caratteri ISO-8859-1 negli header. Filename o path Dropbox con accenti
// italiani (Beautyé fusion, Cliché, Caffè, ecc.) farebbero esplodere
// fetch() con "String contains non ISO-8859-1 code point". Dropbox stesso
// raccomanda di escapare i code-point >127 in \uXXXX prima dell'invio.
function _ascii(str) {
  return str.replace(/[\u007F-\uFFFF]/g, (c) =>
    "\\u" + ("0000" + c.charCodeAt(0).toString(16)).slice(-4)
  );
}

// Dropbox nests `correct_offset` at different depths depending on the
// endpoint (append_v2 vs finish). Deep-search the parsed error object.
function _findCorrectOffset(obj) {
  if (!obj || typeof obj !== "object") return null;
  if (typeof obj.correct_offset === "number") return obj.correct_offset;
  for (const k of Object.keys(obj)) {
    const v = _findCorrectOffset(obj[k]);
    if (v !== null) return v;
  }
  return null;
}

/**
 * POST one Dropbox content call with retry + resume awareness.
 * Returns `{ data }` on success or `{ correctOffset }` when Dropbox says
 * our offset is stale (caller must resume from there). Throws on
 * unrecoverable errors.
 */
async function _dbxCall(endpoint, accessToken, apiArg, body) {
  let lastErr = null;
  for (let attempt = 1; attempt <= MAX_CHUNK_ATTEMPTS; attempt++) {
    let res;
    try {
      res = await fetch(`${DROPBOX_CONTENT}${endpoint}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/octet-stream",
          "Dropbox-API-Arg": _ascii(JSON.stringify(apiArg)),
        },
        body,
      });
    } catch (netErr) {
      // Network drop / phone switched cell → retry with backoff.
      lastErr = new Error(
        "Connessione persa durante il caricamento. Controlla la rete e riprova."
      );
      if (attempt < MAX_CHUNK_ATTEMPTS) {
        await sleep(1000 * Math.pow(2, attempt - 1)); // 1s, 2s, 4s
        continue;
      }
      throw lastErr;
    }
    if (res.ok) {
      const txt = await res.text();
      return { data: txt ? JSON.parse(txt) : {} };
    }
    const text = await res.text().catch(() => "");
    let parsed = null;
    try {
      parsed = JSON.parse(text);
    } catch (_) {
      /* not JSON */
    }
    const correctOffset = _findCorrectOffset(parsed);
    if (correctOffset !== null) {
      // A previous "failed" attempt actually landed — resume from there.
      return { correctOffset };
    }
    if (RETRYABLE_STATUS.has(res.status) && attempt < MAX_CHUNK_ATTEMPTS) {
      await sleep(1500 * attempt);
      continue;
    }
    throw new Error(
      `Dropbox ${res.status}: ${text.slice(0, 200) || res.statusText}`
    );
  }
  throw lastErr || new Error("Upload fallito dopo vari tentativi");
}

/**
 * Shared chunked upload core: opens an upload session, appends 8 MB
 * chunks (with retry/resume), commits the file. Returns
 * `{ pathLower }` — the actual Dropbox path after autorename.
 */
async function _chunkedUpload({ file, accessToken, dropboxPath, chunkSize, reportProgress }) {
  const total = file.size;
  // Uniform protocol: empty start → N appends → empty finish. One extra
  // request vs packing bytes into start/finish, but every byte then goes
  // through the SAME resume-capable append path (no edge cases).
  const startRes = await _dbxCall(
    "/2/files/upload_session/start",
    accessToken,
    { close: false },
    new Blob([]),
  );
  const sessionId = startRes.data.session_id;
  let offset = 0;
  let actualDropboxPath = dropboxPath;

  for (;;) {
    while (offset < total) {
      const end = Math.min(offset + chunkSize, total);
      const chunk = file.slice(offset, end);
      const r = await _dbxCall(
        "/2/files/upload_session/append_v2",
        accessToken,
        { cursor: { session_id: sessionId, offset }, close: false },
        chunk,
      );
      if (r.correctOffset !== undefined) {
        offset = r.correctOffset; // resume from what Dropbox actually has
      } else {
        offset = end;
      }
      reportProgress((offset / total) * 99); // keep 100% for the commit
    }
    const fin = await _dbxCall(
      "/2/files/upload_session/finish",
      accessToken,
      {
        cursor: { session_id: sessionId, offset },
        commit: {
          path: dropboxPath,
          mode: "add",
          autorename: true,
          mute: true,
        },
      },
      new Blob([]),
    );
    if (fin.correctOffset !== undefined) {
      // Session has fewer bytes than we thought — top it up and re-finish.
      offset = fin.correctOffset;
      continue;
    }
    if (fin.data && fin.data.path_lower) {
      actualDropboxPath = fin.data.path_lower;
    }
    reportProgress(100);
    return { pathLower: actualDropboxPath };
  }
}

/**
 * Retry the backend finalize call. The file is ALREADY safe on Dropbox
 * at this point: a failure here must not read as "video perso".
 */
async function _finalizeWithRetry(doFinalize) {
  let lastErr = null;
  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      return await doFinalize();
    } catch (e) {
      lastErr = e;
      const status = e?.response?.status;
      // 4xx (except 429) won't heal with a retry.
      if (status && status >= 400 && status < 500 && status !== 429) break;
      if (attempt < 3) await sleep(2000 * attempt);
    }
  }
  const err = new Error(apiError(lastErr));
  err.uploadedToDropbox = true; // reconciler will rescue it server-side
  throw err;
}

function _mkProgress(onProgress) {
  return (p) => {
    if (typeof onProgress === "function") {
      try {
        onProgress(Math.max(0, Math.min(100, Math.round(p))));
      } catch (e) {
        // never let user-supplied progress break the upload
      }
    }
  };
}

export async function uploadToDropbox({
  kind,
  file,
  clientId = null,
  targetId = null,
  onProgress,
}) {
  if (!file) throw new Error("Nessun file selezionato");
  const reportProgress = _mkProgress(onProgress);
  reportProgress(0);

  // 1. Reserve the slot on our backend.
  let ticket;
  try {
    const { data } = await api.post("/staff/uploads/start", {
      kind,
      filename: file.name,
      size: file.size,
      mime_type: file.type || "application/octet-stream",
      target_id: targetId || undefined,
      client_id: clientId || undefined,
    });
    ticket = data;
  } catch (e) {
    throw new Error(apiError(e));
  }

  // 2. Chunked Browser → Dropbox (hardened).
  const { pathLower } = await _chunkedUpload({
    file,
    accessToken: ticket.access_token,
    dropboxPath: ticket.dropbox_path,
    chunkSize: ticket.chunk_size_recommended,
    reportProgress,
  });

  // 3. Tell our backend to finalise (= verify Dropbox + persist DB row).
  return _finalizeWithRetry(async () => {
    const { data: finRes } = await api.post("/staff/uploads/finalize", {
      ticket_id: ticket.ticket_id,
      dropbox_path: pathLower,
      size: file.size,
    });
    return finRes;
  });
}

/**
 * Same hardened dance, but through the magic-link endpoints
 * (`/api/upload-link/{token}/start|finalize`). No auth required — the
 * token IS the auth. Used by the public /upload/:token page.
 */
export async function uploadViaMagicLink({ magicToken, file, onProgress, sessionId, sequence, label }) {
  if (!file) throw new Error("Nessun file selezionato");
  if (!magicToken) throw new Error("Link mancante");
  const reportProgress = _mkProgress(onProgress);
  reportProgress(0);

  // 1. Reserve slot via magic link.
  let ticket;
  try {
    const { data } = await api.post(`/upload-link/${magicToken}/start`, {
      filename: file.name,
      size: file.size,
      mime_type: file.type || "application/octet-stream",
      // Same session_id across every clip of this "one open link"
      // session — the backend uses it to build the Dropbox ZIP group.
      // Optional: legacy clients still work via time-window fallback.
      ...(sessionId ? { session_id: sessionId } : {}),
      // v1.40 — ordering + optional label typed by the client in the
      // picker list. Backend bakes them into the Dropbox filename
      // (`03 - intro - IMG_0421.mp4`) and onto the message doc so the
      // Produzione board can render a "03 · intro" badge.
      ...(sequence ? { sequence } : {}),
      ...(label ? { label } : {}),
    });
    ticket = data;
  } catch (e) {
    throw new Error(apiError(e));
  }

  // 2. Chunked Browser → Dropbox (hardened).
  const { pathLower } = await _chunkedUpload({
    file,
    accessToken: ticket.access_token,
    dropboxPath: ticket.dropbox_path,
    chunkSize: ticket.chunk_size_recommended,
    reportProgress,
  });

  // 3. Finalise → creates the `messages` row (sender_role=client).
  return _finalizeWithRetry(async () => {
    const { data: finRes } = await api.post(
      `/upload-link/${magicToken}/finalize`,
      {
        ticket_id: ticket.ticket_id,
        dropbox_path: pathLower,
        size: file.size,
      },
    );
    return finRes;
  });
}
